import { Link } from "react-router";
import {
  ArrowRight,
  Cloud,
  Mail,
  MessageSquare,
  Network,
  Sparkles,
} from "lucide-react";

type IntegrationStatus = "connected" | "not_configured" | "coming_soon";
type Provider =
  | "hubspot"
  | "slack"
  | "crossbeam"
  | "teams"
  | "mcp"
  | "powerbi"
  | "email";

// SPA port of /app/admin/settings/integrations/_components/IntegrationCard.tsx.

export function IntegrationCard({
  name,
  description,
  status,
  configureHref,
  provider,
}: {
  name: string;
  description: string;
  status: IntegrationStatus;
  configureHref?: string;
  provider: Provider;
}) {
  return (
    <article className="flex items-start gap-3 rounded-lg border bg-card px-4 py-3">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <ProviderIcon provider={provider} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-display text-base tracking-tight text-foreground">
            {name}
          </h3>
          <StatusBadge status={status} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        <div className="mt-3">
          {status === "coming_soon" ? (
            <span className="text-[11px] text-muted-foreground">Coming soon</span>
          ) : configureHref ? (
            <Link
              to={configureHref}
              className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
            >
              Configure
              <ArrowRight className="h-3 w-3" />
            </Link>
          ) : (
            <span className="text-[11px] text-muted-foreground">
              Set via environment variables
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

function StatusBadge({ status }: { status: IntegrationStatus }) {
  switch (status) {
    case "connected":
      return (
        <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">
          Connected
        </span>
      );
    case "not_configured":
      return (
        <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
          Not configured
        </span>
      );
    case "coming_soon":
      return (
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
          Coming soon
        </span>
      );
  }
}

function ProviderIcon({ provider }: { provider: Provider }) {
  switch (provider) {
    case "hubspot":
      return <Cloud className="h-4 w-4" />;
    case "slack":
    case "teams":
      return <MessageSquare className="h-4 w-4" />;
    case "crossbeam":
      return <Network className="h-4 w-4" />;
    case "mcp":
    case "powerbi":
      return <Sparkles className="h-4 w-4" />;
    case "email":
      return <Mail className="h-4 w-4" />;
  }
}
